// Pricing migration 18 (ADO-2249, Task 3): the product card's price, explained (AC1 to AC3;
// Figma set 1, the v9 proposal board).
//
// A card-only wrapper around row 17's `ProductPrice`: the typeahead and the substitutes panel
// render `ProductPrice` itself, without this wrapper's explanation (Task 5.2).
//
// - Flag off, or flag on and not priced (loading, unpriced, failed, nothing asked):
//   `ProductPrice` alone, exactly as row 17 renders it (the price states, ADO-2248 PS-1).
// - Flag on and priced: a promotion's price is red; the case was price, when the quote has one,
//   is struck above it; a deposit above zero shows under it as `+ €3.00 deposit`.
// - What screen readers hear (DEC-15): a promotion or contract price is read out in words
//   (`Promotion. Was €21.65, now €16.79`, `Contract price. €8.75`) and the figures it replaces,
//   the struck one included, are hidden from assistive tech, so nothing is read twice. A
//   standard or WOW price (DEC-9) is read as the figure, as today.

import { formatMoney, type MoneyValue } from '../shared/money';
import { explainedPrice, shownEntry } from './priceExplanation';
import { ProductPrice } from './ProductPrice';
import { realPricesEnabled } from './realPrices';
import { usePriceEntry } from './usePriceStore';
import styles from './CardPrice.module.css';

export interface CardPriceProps {
  readonly sku: string;
  /** The product's name, for the failed state's Retry (ADO-2248 PS-7). */
  readonly productName: string;
  /** The card's own price class (type, colour, size). */
  readonly className?: string | undefined;
  /** Where the was price, the price and the deposit line up: the grid card's left edge (the
   *  default) or the list card's right edge, beside its action (v9, 7176:6629). */
  readonly align?: 'start' | 'end';
  /**
   * Story 7.32 (ADO-1115), AC1: a Featured Line's charge, as the server quoted it for the
   * Trading Account from the pricing authority. With real prices off it is shown in place of the
   * provisional stand-in, which is never presented as a Featured Line's charge; with them on, the
   * price store's figure (the same authority, the same account) is shown, as on every card.
   */
  readonly accountCharge?: MoneyValue | undefined;
}

export function CardPrice({
  sku,
  productName,
  className,
  align = 'start',
  accountCharge,
}: CardPriceProps) {
  const entry = shownEntry(usePriceEntry(sku));
  if (!realPricesEnabled() && accountCharge !== undefined) {
    return (
      <p className={className} data-price-state="account-charge">
        {formatMoney(accountCharge)}
      </p>
    );
  }
  if (!realPricesEnabled() || entry === null) {
    return <ProductPrice sku={sku} productName={productName} as="p" className={className} />;
  }

  const { promotion, wasPrice, spoken, deposit } = explainedPrice(entry);

  return (
    <div className={styles.block} data-price-kind={entry.kind} data-align={align}>
      <div className={styles.figures} aria-hidden={spoken === null ? undefined : 'true'}>
        {wasPrice !== undefined && <s className={styles.was}>{formatMoney(wasPrice)}</s>}
        <ProductPrice
          sku={sku}
          productName={productName}
          as="p"
          className={
            promotion ? [className, styles.promotion].filter(Boolean).join(' ') : className
          }
        />
      </div>
      {spoken !== null && <span className={styles.visuallyHidden}>{spoken}</span>}
      {deposit !== null && <p className={styles.deposit}>{deposit}</p>}
    </div>
  );
}
